import React, {
  CSSProperties,
  forwardRef,
  MouseEvent,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { Company, Person } from "@mapmycustomers/shared/types/entity";
import { EntityPin } from "@mapmycustomers/shared/types/map";
import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import { getPreviewEntityUniqueElementId, stopEvents } from "@mapmycustomers/shared/util/browser";

import ActivityTypeChip from "@app/component/activity/ActivityTypeChip";
import EntityChip from "@app/component/EntityChip";
import LocationMiniMap from "@app/component/field/LocationMiniMap";
import Lexer from "@app/component/Lexer";
import MultilineText from "@app/component/typography/MultilineText";
import MarkerEffect from "@app/enum/MarkerEffect";
import { showEntityView } from "@app/store/entityView/actions";
import EntityViewPayload from "@app/store/entityView/EntityViewPayload";
import { getMapSettings } from "@app/store/iam";
import { getAllTeamsOfEntireOrganization, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import { TimelineEventActivityCompleted } from "@app/types/events/TimelineEvent";
import getContrastColor from "@app/util/colors/getContrastColor";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";

import BaseEvent from "./BaseEvent";
import CheckInMapDetails from "./components/CheckInMapDetails";
import styles from "./RecordEvent.module.scss";
import getPinFillColor from "./utils/getPinFillColor";

export interface RecordEventProps {
  event: TimelineEventActivityCompleted;
  expanded: boolean;
  onSizeChange?: () => void;
  style?: CSSProperties;
}

interface Props extends RecordEventProps {
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
  onShowEntityView: (payload: EntityViewPayload) => void;
  teams: Team[];
  users: User[];
}

const ActivityCompletedEvent = forwardRef<HTMLDivElement, Props>(
  ({ event, expanded, mapSettings, onShowEntityView, onSizeChange, style, teams, users }, ref) => {
    const intl = useIntl();

    const activityTypeChipRef = useRef<HTMLDivElement>(null);
    const handleActivityClick = useCallback(
      (_, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        const elementId = getPreviewEntityUniqueElementId(activityTypeChipRef.current || undefined);
        onShowEntityView({ elementId, entityId: event.entity.id, entityType: event.entity.entity });
      },
      [event, onShowEntityView]
    );

    const handleShowEntity = useCallback(
      (payload: EntityViewPayload, e?: MouseEvent<HTMLElement>) => {
        stopEvents(e);
        onShowEntityView(payload);
      },
      [onShowEntityView]
    );

    const verified = !!event.entity.reliability;

    // have to embed entity type here since platform doesn't provide it
    const record: Pick<Company | Person, "entity" | "geoPoint" | "id" | "name"> | undefined =
      useMemo(
        () =>
          event.entity.account
            ? { ...event.entity.account, entity: EntityType.COMPANY }
            : event.entity.contact
            ? { ...event.entity.contact, entity: EntityType.PERSON }
            : undefined,
        [event.entity.account, event.entity.contact]
      );

    const entityPinStyleGetter = useMemo(() => {
      const styleGetter = defaultEntityStyleGetter({
        colorGetter: () => {
          // TODO: add color&shape when platform returns them
          const fillColor = getPinFillColor(record?.entity);
          return {
            color: getContrastColor(fillColor) === "black" ? colors.slate : colors.white,
            fillColor,
          };
        },
        draggable: false,
        effect: MarkerEffect.SHADOW,
        icon: () => (mapSettings.showEntityIcons ? record?.entity : undefined),
        markerSize: mapSettings.markerSize,
        // TODO: add color&shape when platform returns them
        // shape: () => record.shape ?? MarkerShape.SQUARE,
        shape: () => MarkerShape.SQUARE,
      });
      // FIXME: generalize defaultEntityStyleGetter so that we can use it with other types in addition to EntityPin
      // For now this is a fake call to style getter. We do not use given argument inside that style getter completely
      return () => styleGetter({} as EntityPin);
    }, [record, mapSettings]);

    return (
      <BaseEvent
        className={styles.mapContainer}
        date={event.updatedAt}
        expanded={expanded}
        extra={
          !record?.geoPoint ? undefined : (
            <LocationMiniMap
              className={styles.map}
              coordinates={record.geoPoint.coordinates}
              styleGetter={entityPinStyleGetter}
            >
              <CheckInMapDetails
                checkInAccuracy={event.entity.distanceBetweenCheckInAndEntity}
                checkInLocation={event.entity.geoPointCheckin}
                recordLocation={record?.geoPoint}
                verified={verified}
              />
            </LocationMiniMap>
          )
        }
        onSizeChange={onSizeChange}
        ref={ref}
        style={style}
        title={intl.formatMessage(
          {
            id: "dashboard.timeline.event.checkIn",
            defaultMessage:
              "logged a {verified, select, true {<b>verified check-in</b>} other {check-in}} {activityType}{record, select, false {} other { at {record}}}{verified, select, true {} other {but it's <b>not verified</b>}}",
            description: "Title of the Check-In event item",
          },
          {
            activityType: (
              <ActivityTypeChip
                activityType={event.entity.crmActivityType}
                isAutoEmail={!!event.entity.emailLog}
                onClick={handleActivityClick}
                ref={activityTypeChipRef}
              />
            ),
            b: (text: ReactNode[]) => <b>{text}</b>,
            record: !record ? (
              false
            ) : (
              <EntityChip
                entityId={record.id}
                entityType={record.entity}
                inline
                name={record.name}
                onClick={handleShowEntity}
              />
            ),
            verified,
          }
        )}
        user={event.updatedBy}
      >
        {event.entity.note ? (
          <div>
            <MultilineText wrapLinesWith={Lexer} wrapperProps={{ teams, users }}>
              {event.entity.note}
            </MultilineText>
          </div>
        ) : null}
      </BaseEvent>
    );
  }
);

const mapStateToProps = (state: RootState) => ({
  mapSettings: getMapSettings(state),
  teams: getAllTeamsOfEntireOrganization(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(
  ActivityCompletedEvent
);
